import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';

export const GoalRiskQuestionVisual = ({
  options,
  goalData,
  loading,
  updateGoal,
  continueFunc,
  workflowCompletion,
}: {
  options: any;
  goalData: any;
  loading: boolean;
  updateGoal: any;
  continueFunc: any;
  workflowCompletion?: any;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'shared']);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      riskQuestion1: { required: true },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        riskQuestion1: goalData.riskQuestion1,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, goalData],
  );

  const handleRiskQuestion1Change = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFocused([...focused, 'riskQuestion1']);
    updateGoal({ ...goalData, riskQuestion1: e.target.value });
  };

  const submit = async () => {
    const formErrors = validate();
    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    sendAnalytic(
      ovAnalyticsEvents.workflowsGoalRiskQuestion1ContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );
    await continueFunc();
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='goal-risk-question-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: `goal-risk-question-${kebabCase(translateBackendString(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      <SelectionTile
        testId='goal-risk-question-1'
        onChange={handleRiskQuestion1Change}
        value={goalData.riskQuestion1 ?? ''}
        options={options?.options?.map((option: any) => ({
          value: option.value,
          label: translateBackendString(option.label),
          subtitle: translateBackendString(option.description),
        }))}
        error={errors?.riskQuestion1?.active}
      />
    </WorkflowCompletionForm>
  );
};

export default GoalRiskQuestionVisual;
